/**
 * Sidebar — navegação lateral fixa.
 *
 * Renderiza itens de navegação filtrados por:
 *   1. modo de autenticação (jwt vs basic)
 *   2. flag de permissão do perfil ativo (via `usePermission`)
 *
 * Spec §10 (Layout Global) e §18 (Renderização Baseada em Permissões — tabela
 * de itens da sidebar).
 *
 * Comportamento por modo:
 *   • `auth.mode === 'jwt'`   → itens regulares (Dashboard, Test Cases, …),
 *                                seção Admin oculta.
 *   • `auth.mode === 'basic'` → APENAS itens admin: Organizations, Tenants,
 *                                Workers, Billing Global, Auditoria de Acesso. Sem JWT, /me e
 *                                /profiles não estão disponíveis — não há
 *                                gating por permissão neste modo.
 *
 * Item ativo: usamos `NavLink` do React Router; a função `className` recebe
 * `{ isActive }` e adiciona um estilo de highlight (bg + texto).
 */
import { NavLink } from 'react-router-dom'
import {
  Cpu,
  Building2,
  BookOpenText,
  Activity,
  CalendarClock,
  Database,
  FileCode2,
  Folders,
  FunctionSquare,
  History,
  Import,
  Layers,
  LayoutDashboard,
  ListChecks,
  Mic2,
  PanelLeftClose,
  PanelLeftOpen,
  PlayCircle,
  Receipt,
  ServerCog,
  ShieldCheck,
  Smartphone,
  UserCog,
  KeyRound,
  Users,
  Wallet,
  FileSearch,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth.store'
import { useSidebarStore } from '@/store/sidebar.store'

import { usePermission, type PermissionFlag } from '@/hooks/usePermission'

// ---------------------------------------------------------------------------
// Modelo de item
// ---------------------------------------------------------------------------

interface SidebarItem {
  /** Path do React Router. */
  to: string
  /** Chave i18n em `common:navigation.<key>`. */
  i18nKey: string
  /** Ícone Lucide. */
  icon: LucideIcon
  /**
   * Flag de permissão exigida. `null` significa "sempre visível" (Dashboard).
   * Para admin (modo basic), a flag é ignorada — itens admin não passam por
   * `usePermission` (não há /profiles disponível).
   */
  flag: PermissionFlag | null
  /** Condição extra arbitrária, avaliada além da flag de permissão. */
  extraVisible?: boolean
  /** Testid estável para os testes. */
  testid: string
}

// ---------------------------------------------------------------------------
// Item de navegação (com highlight do item ativo)
// ---------------------------------------------------------------------------

interface NavItemProps {
  item: SidebarItem
  label: string
  /** Quando `true`, mostra só o ícone (label reaparece no hover da rail). */
  collapsed?: boolean
}

function NavItem({ item, label, collapsed = false }: NavItemProps): React.ReactElement {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      data-testid={item.testid}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          // Colapsado: ícone centralizado; volta a alinhar à esquerda no hover.
          collapsed && 'justify-center group-hover/sidebar:justify-start',
          isActive &&
            'bg-accent text-accent-foreground data-[active=true]:bg-accent',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* `data-active` permite que os testes verifiquem highlight sem
              depender de classes específicas. */}
          <span data-active={isActive} className="sr-only">
            {isActive ? 'active' : 'inactive'}
          </span>
          <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span
            className={cn(
              'truncate',
              collapsed && 'hidden group-hover/sidebar:inline',
            )}
          >
            {label}
          </span>
        </>
      )}
    </NavLink>
  )
}

// ---------------------------------------------------------------------------
// Wrapper que aplica gating de permissão (modo JWT apenas)
// ---------------------------------------------------------------------------

interface GatedNavItemProps {
  item: SidebarItem
  label: string
  collapsed?: boolean
}

/**
 * Aplica `usePermission` no item se ele exigir uma flag. Renderiza `null`
 * quando a flag está desabilitada (fail-closed pela própria `usePermission`)
 * ou quando `extraVisible === false`.
 *
 * O hook `usePermission` é chamado incondicionalmente (mesmo quando `flag`
 * é null) para respeitar as regras de hooks; nesse caso passamos uma flag
 * "neutra" e ignoramos o resultado.
 */
function GatedNavItem({
  item,
  label,
  collapsed,
}: GatedNavItemProps): React.ReactElement | null {
  // Hook sempre chamado — passa flag dummy quando não há gating.
  const hasFlagPermission = usePermission(item.flag ?? 'can_view_tests')
  if (item.extraVisible === false) return null
  if (item.flag !== null && !hasFlagPermission) return null
  return <NavItem item={item} label={label} collapsed={collapsed} />
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

export interface SidebarProps {
  className?: string
}

export function Sidebar({ className }: SidebarProps): React.ReactElement {
  const { t } = useTranslation('common')
  const mode = useAuthStore((s) => s.mode)
  const collapsed = useSidebarStore((s) => s.collapsed)
  const toggleCollapsed = useSidebarStore((s) => s.toggle)

  // ── Definição dos itens ───────────────────────────────────────────────
  // Conforme spec §18 — tabela "Item da Sidebar / Condição de visibilidade".

  const primaryItems: SidebarItem[] = [
    {
      to: '/',
      i18nKey: 'navigation.dashboard',
      icon: LayoutDashboard,
      flag: null, // Sempre visível em modo JWT.
      testid: 'sidebar-item-dashboard',
    },
    {
      to: '/observability',
      i18nKey: 'navigation.observability',
      icon: Activity,
      flag: 'can_view_reports',
      testid: 'sidebar-item-observability',
    },
    {
      to: '/test-cases',
      i18nKey: 'navigation.testCases',
      icon: FileCode2,
      flag: 'can_view_tests',
      testid: 'sidebar-item-test-cases',
    },
    {
      to: '/test-suites',
      i18nKey: 'navigation.testSuites',
      icon: Folders,
      flag: 'can_view_tests',
      testid: 'sidebar-item-test-suites',
    },
    {
      to: '/imports',
      i18nKey: 'navigation.imports',
      icon: Import,
      flag: 'can_create_test',
      testid: 'sidebar-item-imports',
    },
    {
      to: '/datasets',
      i18nKey: 'navigation.datasets',
      icon: Database,
      flag: 'can_view_tests',
      testid: 'sidebar-item-datasets',
    },
    {
      to: '/functions',
      i18nKey: 'navigation.functions',
      icon: FunctionSquare,
      flag: 'can_view_tests',
      testid: 'sidebar-item-functions',
    },
    {
      to: '/curadoria',
      i18nKey: 'navigation.curadoria',
      icon: FileSearch,
      flag: 'can_view_reports',
      testid: 'sidebar-item-curadoria',
    },
    {
      to: '/journey-profiles',
      i18nKey: 'navigation.journeyProfiles',
      icon: BookOpenText,
      flag: 'can_view_tests',
      testid: 'sidebar-item-journey-profiles',
    },
    {
      to: '/app-profiles',
      i18nKey: 'navigation.appProfiles',
      icon: Layers,
      flag: 'can_view_tests',
      testid: 'sidebar-item-app-profiles',
    },
    {
      to: '/devices',
      i18nKey: 'navigation.devices',
      icon: Smartphone,
      flag: 'can_view_tests',
      testid: 'sidebar-item-devices',
    },
    {
      to: '/schedules',
      i18nKey: 'navigation.schedules',
      icon: CalendarClock,
      flag: 'can_manage_schedules',
      testid: 'sidebar-item-schedules',
    },
    {
      to: '/executions',
      i18nKey: 'navigation.executions',
      icon: ListChecks,
      flag: 'can_view_reports',
      testid: 'sidebar-item-executions',
    },
    {
      to: '/preview',
      i18nKey: 'navigation.livePreview',
      icon: PlayCircle,
      flag: 'can_execute_test',
      testid: 'sidebar-item-preview',
    },
  ]

  const secondaryItems: SidebarItem[] = [
    {
      to: '/billing',
      i18nKey: 'navigation.billing',
      icon: Receipt,
      flag: 'can_view_billing',
      testid: 'sidebar-item-billing',
    },
    {
      to: '/voice-samples',
      i18nKey: 'navigation.voiceSamples',
      icon: Mic2,
      flag: 'can_view_tests',
      // Antes era oculto quando nenhum Worker tinha XTTS. Clonagem deixou de
      // ser capability opcional — todo Worker clona —, então o item vive só
      // sob a permissão.
      testid: 'sidebar-item-voice-samples',
    },
    {
      to: '/settings/users',
      i18nKey: 'navigation.users',
      icon: Users,
      flag: 'can_manage_users',
      testid: 'sidebar-item-users',
    },
    {
      to: '/settings/profiles',
      i18nKey: 'navigation.profiles',
      icon: UserCog,
      flag: 'can_manage_users',
      testid: 'sidebar-item-profiles',
    },
    {
      // Sem flag: todo usuário gere as próprias chaves (escopo ⊆ o dele).
      to: '/settings/api-keys',
      i18nKey: 'navigation.apiKeys',
      icon: KeyRound,
      flag: null,
      testid: 'sidebar-item-api-keys',
    },
  ]

  // Itens admin (modo basic apenas).
  const adminItems: SidebarItem[] = [
    {
      to: '/admin/llm',
      i18nKey: 'navigation.llm',
      icon: Cpu,
      flag: null,
      testid: 'sidebar-item-admin-llm',
    },
    {
      to: '/admin/organizations',
      i18nKey: 'navigation.organizations',
      icon: Building2,
      flag: null,
      testid: 'sidebar-item-admin-organizations',
    },
    {
      to: '/admin/tenants',
      i18nKey: 'navigation.tenants',
      icon: ShieldCheck,
      flag: null,
      testid: 'sidebar-item-admin-tenants',
    },
    {
      to: '/admin/users',
      i18nKey: 'navigation.adminUsers',
      icon: Users,
      flag: null,
      testid: 'sidebar-item-admin-users',
    },
    {
      to: '/admin/workers',
      i18nKey: 'navigation.workers',
      icon: ServerCog,
      flag: null,
      testid: 'sidebar-item-admin-workers',
    },
    {
      to: '/admin/billing',
      i18nKey: 'navigation.globalBilling',
      icon: Wallet,
      flag: null,
      testid: 'sidebar-item-admin-billing',
    },
    {
      to: '/admin/access-events',
      i18nKey: 'navigation.accessEvents',
      icon: History,
      flag: null,
      testid: 'sidebar-item-admin-access-events',
    },
  ]

  // ── Render ────────────────────────────────────────────────────────────

  return (
    // Spacer em fluxo reserva a largura da rail; o <aside> é posicionado
    // absolutamente sobre ele para que o hover-expand (overlay) NÃO empurre o
    // conteúdo da página.
    <div
      data-testid="sidebar-slot"
      data-collapsed={collapsed}
      className={cn(
        'relative h-full shrink-0 transition-[width] duration-200 ease-out',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      <aside
        data-testid="sidebar"
        data-auth-mode={mode ?? 'none'}
        className={cn(
          'group/sidebar absolute inset-y-0 left-0 z-30 flex h-full flex-col border-r border-border bg-card',
          'transition-[width] duration-200 ease-out',
          collapsed ? 'w-16 hover:w-60 hover:shadow-xl' : 'w-60',
          className,
        )}
      >
        {/* Cabeçalho com o mini-botão de colapsar (presente em todas as telas). */}
        <div
          className={cn(
            'flex h-12 shrink-0 items-center border-b border-border px-3',
            collapsed ? 'justify-center group-hover/sidebar:justify-end' : 'justify-end',
          )}
        >
          <button
            type="button"
            data-testid="sidebar-toggle"
            onClick={toggleCollapsed}
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            aria-expanded={!collapsed}
            title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
            ) : (
              <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-3 py-4">
          {mode === 'basic' ? (
            // Modo admin global — APENAS itens admin (sem usePermission).
            <div
              data-testid="sidebar-section-admin"
              className="space-y-1"
              aria-label={t('navigation.admin')}
            >
              <p
                className={cn(
                  'px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground',
                  collapsed && 'hidden group-hover/sidebar:block',
                )}
              >
                {t('navigation.admin')}
              </p>
              {adminItems.map((item) => (
                <NavItem
                  key={item.to}
                  item={item}
                  label={t(item.i18nKey)}
                  collapsed={collapsed}
                />
              ))}
            </div>
          ) : (
            // Modo JWT — itens regulares com gating por permissão. Seção admin
            // permanece oculta (Spec §18: "Sidebar modo jwt oculta seção Admin").
            <>
              <div data-testid="sidebar-section-primary" className="space-y-1">
                {primaryItems.map((item) => (
                  <GatedNavItem
                    key={item.to}
                    item={item}
                    label={t(item.i18nKey)}
                    collapsed={collapsed}
                  />
                ))}
              </div>

              <div
                role="separator"
                aria-hidden="true"
                className="my-3 h-px bg-border"
              />

              <div data-testid="sidebar-section-secondary" className="space-y-1">
                {secondaryItems.map((item) => (
                  <GatedNavItem
                    key={item.to}
                    item={item}
                    label={t(item.i18nKey)}
                    collapsed={collapsed}
                  />
                ))}
              </div>
            </>
          )}
        </nav>
      </aside>
    </div>
  )
}
